import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { differenceInDays, startOfDay } from 'date-fns';
import { useContext } from 'react';
import styled from 'styled-components';
import { useAutopayStatusMutation } from '@syf-api-library/autopay-status';
import { Button } from 'syf-component-library/ui/atoms';
import DotLoader from 'syf-component-library/ui/atoms/Loader/DotLoader';
import { Grid } from 'syf-component-library/ui/patterns';
import { Inset, Stack } from 'syf-component-library/ui/spacing';
import { Body } from 'syf-component-library/ui/typography';
import formatDateStringToObject from 'syf-js-utilities/formats/formatDateStringToObject';
import { DATA_ANALYTICS, DATA_TYPE, PAGE_FUNCTION, PAGE_NAME, SUB_FUNCTION } from '../../../../const/analytics';
import MainContext from '../../../../Context';
import { colors } from '../../../../defaultTheme';
import { getEstDateObject, isMonthly } from '../../../../helpers';
import useApiMachine from '../../../../hooks/machines/useApiMachine';
import useAutopayBannerMachine from '../../../../hooks/machines/useAutopayBannerMachine';
import usePaymentFlowMachine from '../../../../hooks/machines/usePaymentFlowMachine';
import { useApiConfig } from '../../../../hooks/useApiConfig';
import { t } from '../../../../strings/i18n';
const CancelAutopayBannerWrapper = styled.div `
  background-color: ${colors.lightGrey};
  border-bottom: 1px solid ${colors.mediumGrey};
`;
const TurnAutopayOffButton = styled(Button) `
  text-transform: uppercase;
  height: 48px;
`;
const CancelAutopayAdvice = () => {
    return (_jsxs(_Fragment, { children: [_jsx(Stack, { size: "medium" }), _jsx(Body, Object.assign({ "data-cy": "turn-autopay-off-advice", weight: "bold" }, { children: t('autopayCollapseBanner.cancelAutopayAdvice') }))] }));
};
const CancelAutopayText = ({ isPayLaterMonthly, isWithin48HourWindow }) => {
    if (isPayLaterMonthly) {
        if (isWithin48HourWindow)
            return (_jsx(_Fragment, { children: t('autopayCollapseBanner.cancelAutopayTextWithin48hoursMonthly') }));
        return _jsx(_Fragment, { children: t('autopayCollapseBanner.cancelAutopayTextPayLaterMonthly') });
    }
    return _jsx(_Fragment, { children: t('autopayCollapseBanner.cancelAutopayText') });
};
const CancelAutopayBanner = () => {
    const { triggerAnalyticsEvent } = useContext(MainContext);
    const apiConfig = useApiConfig();
    const [apiMachine] = useApiMachine();
    const { accountSummaryQueryResult, autopayStatusQueryResult } = apiMachine.context;
    const { data: accountSummary } = accountSummaryQueryResult;
    const { data: autopayStatus } = autopayStatusQueryResult;
    const productType = accountSummary.product.type;
    const isPayLaterMonthly = isMonthly(productType);
    const autopayStatusMutation = useAutopayStatusMutation(apiConfig);
    const [, , sendToAutopay] = usePaymentFlowMachine();
    const [, sendToAutopayBannerMachine] = useAutopayBannerMachine();
    const turnAutopayOff = async () => {
        var _a, _b;
        try {
            await autopayStatusMutation.mutateAsync({
                enrollment: false
            });
            // Navigate to confirmation screen
            sendToAutopay({ type: 'SUCCESS', successType: 'unenrolling' });
        }
        catch (_c) {
            sendToAutopay({ type: 'ERROR', errorType: 'unenrolling' });
            // TODO: This anlaytic event should be scoped in `autopayMachine` once machines are able to read status codes
            triggerAnalyticsEvent({
                pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.AUTOPAY,
                pageSubFunction: SUB_FUNCTION.AUTOPAY.TURN_OFF_AUTOPAY,
                pageName: `${PAGE_NAME.AUTOPAY.FAILED}:${(_b = (_a = autopayStatusMutation === null || autopayStatusMutation === void 0 ? void 0 : autopayStatusMutation.error) === null || _a === void 0 ? void 0 : _a.response) === null || _b === void 0 ? void 0 : _b.status}`
            });
        }
    };
    const check48HoursWindow = () => {
        // Handle an edge case where the component is transitioning just after the unenroll
        if (autopayStatus.enrollment) {
            const numberOfDaysFromDueDate = differenceInDays(formatDateStringToObject(accountSummary.billing.nextPaymentDate), startOfDay(getEstDateObject(new Date())));
            return numberOfDaysFromDueDate >= 0 && numberOfDaysFromDueDate <= 2;
        }
        return false;
    };
    const keepAutopayOn = () => {
        sendToAutopayBannerMachine({
            type: 'DECLINE_UNENROLL'
        });
    };
    return (_jsx(CancelAutopayBannerWrapper, { children: _jsx(Inset, Object.assign({ bottom: "xlarge", left: "large", right: "large", top: "medium" }, { children: _jsxs(Grid, Object.assign({ container: true }, { children: [_jsxs(Grid, Object.assign({ item: true }, { children: [_jsx(Body, Object.assign({ "data-cy": "turn-autopay-off-heading", weight: "bold" }, { children: t('autopayCollapseBanner.turnAutopayOff') })), _jsx(Stack, { size: "large" }), _jsx(Body, Object.assign({ "data-cy": "turn-autopay-off-info" }, { children: _jsx(CancelAutopayText, { isPayLaterMonthly: isPayLaterMonthly, isWithin48HourWindow: check48HoursWindow() }) })), !isPayLaterMonthly && _jsx(CancelAutopayAdvice, {}), _jsx(Stack, { size: "large" })] })), _jsx(Grid, Object.assign({ alignItems: "center", container: true, item: true, justifyContent: "center", md: 9, wrap: "wrap" }, { children: autopayStatusMutation.isLoading ? (_jsx(DotLoader, {})) : (_jsxs(_Fragment, { children: [_jsx(Grid, Object.assign({ item: true, md: 5 }, { children: _jsx(TurnAutopayOffButton, Object.assign({ buttonType: "text", "data-analytics": DATA_ANALYTICS.CLICK_TRACKING_EVENT, "data-reason": "yes turn autopay off banner", "data-type": DATA_TYPE.MAKE_A_PAYMENT, dataCy: "continue-unenrolling-autopay", onClick: turnAutopayOff }, { children: t('button.yesAutopayOff') })) })), _jsx(Grid, Object.assign({ item: true, md: 5 }, { children: _jsx(Button, Object.assign({ buttonType: "primary", "data-analytics": DATA_ANALYTICS.CLICK_TRACKING_EVENT, "data-reason": "no keep autopay on banner", "data-type": DATA_TYPE.MAKE_A_PAYMENT, dataCy: "stop-unenrolling-autopay", onClick: keepAutopayOn }, { children: t('button.keepAutopayOn') })) }))] })) }))] })) })) }));
};
export default CancelAutopayBanner;
